import React from 'react';
import { Input } from 'reactstrap';
import './MeetingTypeSelector.scss';

export interface MeetingType {
    type: 'inPerson' | 'virtual' | 'combo';
    label: string;
    checked: boolean;
    maxDuration: string;
    error?: boolean;
    errorMessage?: string;
    /** Highlight row orange: meeting type is not in the availability block */
    mismatchTypeNotInBlock?: boolean;
    /** Highlight max length text orange: selected time range is longer than block max for this type */
    mismatchDurationExceedsMax?: boolean;
}

interface MeetingTypeSelectorProps {
    meetingTypes: MeetingType[];
    onChange: (type: 'inPerson' | 'virtual' | 'combo', field: 'checked' | 'maxDuration', value: boolean | string) => void;
    disabled?: boolean;
    readonly?: boolean;
    maxAllowedMinutes?: number;
    error?: string;
    singleSelect?: boolean; // If true, only one meeting type can be selected at a time (radio button behavior)
    /** When true, hide Maximum Meeting Length and show only the selected meeting type label (e.g. for buyerTopLevel) */
    simplified?: boolean;
    /** When true, hide "Maximum Meeting Length" header and duration column (e.g. for seller editing proposal) */
    hideMaximumMeetingLength?: boolean;
}

// Helper to convert time string (H:MM) to minutes
const timeToMinutes = (timeStr: string): number => {
    if (!timeStr) return 0;
    const parts = timeStr.split(':');
    const hours = parseInt(parts[0] || '0', 10);
    const minutes = parseInt(parts[1] || '0', 10);
    return hours * 60 + minutes;
};

// Helper to convert minutes to time string (H:MM)
const minutesToTime = (totalMinutes: number): string => {
    if (totalMinutes <= 0) return '0:00';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

export const MeetingTypeSelector: React.FC<MeetingTypeSelectorProps> = ({
    meetingTypes,
    onChange,
    disabled = false,
    readonly = false,
    maxAllowedMinutes = 720,
    error,
    singleSelect = false,
    simplified = false,
    hideMaximumMeetingLength = false,
}) => {
    const INCREMENT_STEP = 15; // 15 minutes increment
    const MIN_DURATION = 15; // Minimum 15 minutes
    const MAX_DURATION = 720; // Maximum 12 hours (720 minutes)
    
    // Use the smaller of MAX_DURATION and maxAllowedMinutes
    const effectiveMaxDuration = maxAllowedMinutes > 0 ? Math.min(MAX_DURATION, maxAllowedMinutes) : MAX_DURATION;
    
    const hasError = !!error;

    const handleCheckboxChange = (type: 'inPerson' | 'virtual' | 'combo', checked: boolean) => {
        if (singleSelect && checked) {
            // If single select mode and checking a type, uncheck all others first
            meetingTypes.forEach((mt) => {
                if (mt.type !== type && mt.checked) {
                    onChange(mt.type, 'checked', false);
                    onChange(mt.type, 'maxDuration', '');
                }
            });
        }
        onChange(type, 'checked', checked);
        if (!checked) {
            onChange(type, 'maxDuration', '');
        }
    };

    const handleDurationChange = (type: 'inPerson' | 'virtual' | 'combo', value: string) => {
        onChange(type, 'maxDuration', value);
    };

    const handleIncrement = (type: 'inPerson' | 'virtual' | 'combo', currentValue: string) => {
        const currentMinutes = timeToMinutes(currentValue);
        // If empty or 0, start at minimum
        const startMinutes = currentMinutes === 0 ? 0 : currentMinutes;
        const newMinutes = Math.min(startMinutes + INCREMENT_STEP, effectiveMaxDuration);
        const newValue = minutesToTime(newMinutes);
        onChange(type, 'maxDuration', newValue);
    };

    const handleDecrement = (type: 'inPerson' | 'virtual' | 'combo', currentValue: string) => {
        const currentMinutes = timeToMinutes(currentValue);
        if (currentMinutes === 0) return; // Don't decrement from empty
        const newMinutes = Math.max(currentMinutes - INCREMENT_STEP, MIN_DURATION);
        const newValue = minutesToTime(newMinutes);
        onChange(type, 'maxDuration', newValue);
    };

    const handleInputChange = (type: 'inPerson' | 'virtual' | 'combo', value: string) => {
        // Allow user to type, but validate on blur
        onChange(type, 'maxDuration', value);
    };

    const handleInputBlur = (type: 'inPerson' | 'virtual' | 'combo', value: string) => {
        // If empty or invalid, keep it empty
        if (!value || value.trim() === '') {
            onChange(type, 'maxDuration', '');
            return;
        }
        
        // Validate and normalize the input
        const minutes = timeToMinutes(value);
        if (minutes === 0) {
            onChange(type, 'maxDuration', '');
        } else if (minutes < MIN_DURATION) {
            onChange(type, 'maxDuration', minutesToTime(MIN_DURATION));
        } else if (minutes > effectiveMaxDuration) {
            onChange(type, 'maxDuration', minutesToTime(effectiveMaxDuration));
        } else {
            // Round to nearest 15 minutes
            const rounded = Math.round(minutes / INCREMENT_STEP) * INCREMENT_STEP;
            const finalMinutes = Math.min(rounded || MIN_DURATION, effectiveMaxDuration);
            onChange(type, 'maxDuration', minutesToTime(finalMinutes));
        }
    };

    if (simplified) {
        const selectedLabels = meetingTypes.filter((mt) => mt.checked).map((mt) => mt.label);
        const displayLabel = selectedLabels.length > 0 ? selectedLabels.join(', ') : '—';
        return (
            <div className={`meeting-type-selector meeting-type-selector--simplified ${hasError ? 'has-error' : ''}`}>
                <div className="meeting-type-header">
                    <div className="meeting-type-label">
                        Meeting Type
                    </div>
                </div>
                <div className="meeting-type-simplified-value">
                    {displayLabel}
                </div>
            </div>
        );
    }

    return (
        <div className={`meeting-type-selector ${hasError ? 'has-error' : ''} ${hideMaximumMeetingLength ? 'meeting-type-selector--hide-max-length' : ''}`}>
            <div className="meeting-type-header">
                <div className="meeting-type-label">
                    Meeting Type
                </div>
                {!hideMaximumMeetingLength && (
                <div className="maximum-meeting-length-label">
                    Maximum Meeting Length
                </div>
                )}
            </div>
            
            <div className="meeting-type-options">
                {meetingTypes.map((meetingType) => (
                    <div
                        key={meetingType.type}
                        className={`meeting-type-row ${meetingType.mismatchTypeNotInBlock ? 'availability-mismatch-row' : ''}`}
                    >
                        <div className="meeting-type-checkbox-wrapper">
                            <input
                                type="checkbox"
                                id={`meeting-type-${meetingType.type}`}
                                checked={meetingType.checked}
                                onChange={(e) => handleCheckboxChange(meetingType.type, e.target.checked)}
                                disabled={disabled}
                                className={`meeting-type-checkbox ${meetingType.mismatchTypeNotInBlock ? 'availability-mismatch' : ''}`}
                            />
                            <label 
                                htmlFor={`meeting-type-${meetingType.type}`} 
                                className={`meeting-type-checkbox-label ${meetingType.mismatchTypeNotInBlock ? 'availability-mismatch' : ''}`}
                            >
                                {meetingType.label}
                            </label>
                        </div>
                        {!hideMaximumMeetingLength && (
                        <div className={`meeting-type-duration-wrapper ${meetingType.mismatchDurationExceedsMax || meetingType.mismatchTypeNotInBlock ? 'availability-mismatch' : ''}`}>
                            {readonly ? (
                                <div className="duration-readonly">
                                    {meetingType.maxDuration || '0:00'}
                                </div>
                            ) : (
                                <div className={`duration-input-group ${meetingType.error ? 'has-error' : ''}`}>
                                    <button
                                        type="button"
                                        className="duration-button decrement"
                                        onClick={() => handleDecrement(meetingType.type, meetingType.maxDuration || '0:00')}
                                        disabled={disabled || !meetingType.checked || !meetingType.maxDuration || timeToMinutes(meetingType.maxDuration || '0:00') <= MIN_DURATION}
                                        title="Decrease by 15 minutes"
                                    >
                                        −
                                    </button>
                                    <input
                                        type="text"
                                        value={meetingType.maxDuration || '0:00'}
                                        onChange={(e) => handleInputChange(meetingType.type, e.target.value)}
                                        onBlur={(e) => handleInputBlur(meetingType.type, e.target.value)}
                                        disabled={disabled || !meetingType.checked}
                                        placeholder={(disabled || !meetingType.checked) ? "0:00" : "H:MM"}
                                        className="duration-input"
                                    />
                                    <button
                                        type="button"
                                        className="duration-button increment"
                                        onClick={() => handleIncrement(meetingType.type, meetingType.maxDuration || '0:00')}
                                        disabled={disabled || !meetingType.checked || timeToMinutes(meetingType.maxDuration || '0:00') >= effectiveMaxDuration}
                                        title="Increase by 15 minutes"
                                    >
                                        +
                                    </button>
                                </div>
                            )}
                            {meetingType.error && meetingType.errorMessage && (
                                <div className="duration-error-message">{meetingType.errorMessage}</div>
                            )}
                        </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

